import React, { useContext, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbtack } from '@fortawesome/free-solid-svg-icons';
import { SidePanelUiContext } from '@/core/deep-dive/contexts/side-panel-provider';
import WidgetPane from '@/core/deep-dive/components/widget-pane';
import { getDeepDiveApps, isDeepDiveAppVisible } from '@/core/deep-dive/services/app-registry';
import { DeepDiveWidget } from '@/core/widgets/export';
import { useEffectivePermissions } from '@/core/permissions/export';
import styles from './app-rail.module.css';

function AppIcon({
  app,
  active,
  pinned,
  onClick,
}: {
  app: DeepDiveWidget;
  active: boolean;
  pinned: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type={'button'}
      className={`${styles.appTab} ${active ? styles.appTabActive : ''}`}
      onClick={onClick}
    >
      <span className={styles.appIconWrap}>
        <span className={styles.appIcon}>{app.icon}</span>
        {pinned && (
          <span className={styles.pinBadge} title={'Pinned'}>
            <FontAwesomeIcon icon={faThumbtack} />
          </span>
        )}
      </span>
      <span className={styles.appLabel}>{app.label ?? app.defaultTitle}</span>
    </button>
  );
}

export function AppRailBrowseView({ apps }: { apps: DeepDiveWidget[] }) {
  const sidePanelUi = useContext(SidePanelUiContext);

  if (apps.length === 0) {
    return (
      <div className={'text-center text-muted mt-5 small'}>
        <span>No widgets available yet.</span>
      </div>
    );
  }

  // Pinned apps first — quick access without needing a separate spot for them.
  const sortedApps = [...apps].sort((a, b) => {
    const aPinned = sidePanelUi.pinnedAppIds.includes(a.id);
    const bPinned = sidePanelUi.pinnedAppIds.includes(b.id);
    return aPinned === bPinned ? 0 : aPinned ? -1 : 1;
  });

  return (
    <div className={styles.appsRow}>
      {sortedApps.map(app => (
        <AppIcon
          key={app.id}
          app={app}
          active={app.id === sidePanelUi.selectedAppId}
          pinned={sidePanelUi.pinnedAppIds.includes(app.id)}
          onClick={() => sidePanelUi.openApp(app.id)}
        />
      ))}
    </div>
  );
}

function AppRailDetailView({ app }: { app: DeepDiveWidget }) {
  const sidePanelUi = useContext(SidePanelUiContext);

  // Pin/close controls for the selected app now live in the side panel's own
  // header row (see side-panel.tsx) rather than duplicated here.
  return (
    <WidgetPane
      widget={app}
      panelId={'deep-dive'}
      path={sidePanelUi.widgetPath ?? undefined}
      onNavigate={path => sidePanelUi.setWidgetPath(path)}
    />
  );
}

export default function AppRail() {
  const sidePanelUi = useContext(SidePanelUiContext);
  const permissions = useEffectivePermissions();
  // Each app's own gating (via registerDeepDiveApp) already accounts for
  // DEEP_DIVE_ACCESS where it applies — apps registered without it (e.g.
  // Bookmarks) must still show up here for users who lack that permission.
  const apps = useMemo(
    () => getDeepDiveApps().filter(app => isDeepDiveAppVisible(app, permissions)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permissions.join(',')],
  );

  // Only where there's room for it (see autoSelectFirstApp) — browsing
  // defaults to the first app's content instead of a bare "pick something".
  // Gated on openPanelId === 'deep-dive': this component stays mounted (just
  // hidden) while the panel is closed or another tab is active, so without
  // this check it would auto-open the panel as soon as it mounts.
  useEffect(() => {
    if (
      sidePanelUi.autoSelectFirstApp &&
      sidePanelUi.openPanelId === 'deep-dive' &&
      sidePanelUi.isRailVisible &&
      !sidePanelUi.selectedAppId &&
      apps.length > 0
    ) {
      sidePanelUi.openApp(apps[0].id);
    }
  }, [sidePanelUi, apps]);

  if (!sidePanelUi.selectedAppId) {
    return <AppRailBrowseView apps={apps} />;
  }

  const selected = apps.find(app => app.id === sidePanelUi.selectedAppId);
  if (!selected) {
    return <AppRailBrowseView apps={apps} />;
  }

  return (
    <div>
      {sidePanelUi.isRailVisible && <AppRailBrowseView apps={apps} />}
      <AppRailDetailView app={selected} />
    </div>
  );
}
